/** @jsx h */
/** @jsxFrag Fragment */

import { h, Fragment, ComponentProps } from "preact";
import { useCallback } from "preact/hooks";
import { FormAction } from "../formReducer";
import { FormPage } from "../FormTypes";

const MAX_PROGRESS_COLS = 9;

type PageButtonProps = {
  onClick: () => void;
  isComplete: boolean;
};
const PageButton = ({ onClick, isComplete }: PageButtonProps) => {
  return (
    <button
      className={`grow w-full rounded h-2.5 transition bg-white`}
      onClick={() => {
        return isComplete && onClick();
      }}
    >
      <div
        className={`rounded w-full h-full ${
          isComplete ? "bg-brand-primary" : "bg-brand-primary/50"
        }`}
      ></div>
    </button>
  );
};

export type PageProgressProps = {
  dispatch: (action: FormAction) => void;
  pages: FormPage[];
  currentPageId: number;
  className?: string;
};
export const PageProgress = ({
  dispatch,
  currentPageId,
  pages,
  className,
}: PageProgressProps) => {
  const handleClick = useCallback(
    (pageIndex: number) =>
      dispatch({ type: "setPage", page: pages[pageIndex] }),
    [dispatch, pages]
  );

  const currentPageIdx = pages.findIndex((p) => p.id === currentPageId);
  const pageCount = pages.length - 1;
  let maxColumns = Math.min(pageCount, MAX_PROGRESS_COLS);

  // try to find a neater arrangement if possible
  if (pageCount > maxColumns && Math.floor(pageCount / maxColumns) <= 3) {
    let diff =
      Math.ceil(pageCount / MAX_PROGRESS_COLS) * MAX_PROGRESS_COLS - pageCount;

    const twoRows = Math.ceil(pageCount / 2);
    const twoDiff = twoRows * 2 - pageCount;
    if (twoDiff < diff && twoDiff >= 0 && twoRows <= MAX_PROGRESS_COLS) {
      maxColumns = twoRows;
      diff = twoDiff;
    }

    const threeRows = Math.ceil(pageCount / 3);
    const threeDiff = threeRows * 3 - pageCount;
    if (threeDiff < diff && threeDiff >= 0 && threeRows <= MAX_PROGRESS_COLS) {
      maxColumns = threeRows;
      diff = threeDiff;
    }
  }

  return (
    <div
      className={`px-9 grid gap-2 grid-cols-${maxColumns} items-center h-fit mt-4 md:mt-auto mb-auto ${
        className ?? ""
      }`}
    >
      {/** Slice to get every page except the last, so we don't include the post-submission
        screen in the form progress display. **/}
      {pages.slice(0, -1).map((p: FormPage, idx) => {
        const isComplete = idx <= currentPageIdx;
        return (
          <PageButton
            key={p.id}
            isComplete={isComplete}
            onClick={() => handleClick(idx)}
          />
        );
      })}
    </div>
  );
};
